// Peta modul → rute frontend, KHUSUS untuk baris "Aktivitas Edit & Hapus" di
// Dashboard (permintaan owner poin 9). Satu sumber kebenaran — jangan disalin
// ke berkas lain kalau butuh pemetaan yang sama.
//
// KENAPA berkas terpisah: kunci `modul` yang ditulis `catat_aktivitas()` di
// backend (`backend/routers/aktivitas.py`) TIDAK selalu sama dengan path rute
// di `App.js`, dan kadang juga tidak sama dengan kunci izin yang benar-benar
// menggerbangi halaman tujuannya (lihat kasus `my_kpi` di bawah). Tabel ini
// dibangun dengan menyisir SEMUA pemanggilan `catat_aktivitas(...)` di
// `backend/` (bukan tebakan), lalu mencocokkan tiap nilai `modul` dengan rute
// yang benar-benar terdaftar di `App.js` dan kunci izin yang benar-benar
// menggerbangi rute itu di `Layout.jsx`.
//
// Modul yang SENGAJA TIDAK dipetakan di sini (baris aktivitasnya biarkan
// tidak bisa diklik) — tidak ada di tabel `RUTA_AKTIVITAS`:
//   (semua nilai `modul` yang benar-benar dipakai `catat_aktivitas()` per
//   Agustus 2026 sudah punya halaman tujuan; tidak ada yang perlu digantung.
//   Kalau modul baru menyusul kemudian dan belum ada rute yang jelas, JANGAN
//   menebak path-nya — biarkan tidak ada entrinya di sini, baris itu otomatis
//   tidak bisa diklik lewat `rutaUntukAktivitas()` di bawah.)
//
// Setiap entri:
//   list        — path daftar/formulir modul itu. Dipakai untuk SEMUA aksi
//                 "hapus" (dokumennya sudah tidak ada, jadi tidak ada gunanya
//                 mengarah ke halaman detail yang isinya kosong/error), dan
//                 untuk modul yang halamannya memang tidak punya rute detail
//                 per-dokumen (mayoritas — lihat catatan per modul).
//   detail(id)  — fungsi opsional yang membentuk path detail per dokumen.
//                 HANYA diisi kalau halaman tujuannya benar-benar punya rute
//                 `:id` di App.js DAN `target_id` yang dicatat backend adalah
//                 id entitas yang sama dipakai rute itu (bukan id entitas
//                 lain yang kebetulan berbagi modul).
//   permModul   — kunci izin yang menggerbangi halaman tujuan (Layout.jsx),
//                 dipakai untuk `can(permModul, "view")`. Default = kunci
//                 `modul` itu sendiri; diisi eksplisit kalau BEDA (lihat
//                 `my_kpi`).
const RUTA_AKTIVITAS = {
  // ---- POS & Sales ----
  // Input Nota (Sales/Akuisisi): satu halaman gabungan daftar+form, tidak ada
  // rute :id — deep-link ke baris tertentu tidak didukung UI-nya.
  nota_sales: { list: "/input-nota" },
  nota_akuisisi: { list: "/input-nota-akuisisi" },
  // Input Penjualan: juga menaungi log "config:..." (saklar darurat Sumber
  // Info) — bukan id transaksi, tapi modul ini toh cuma punya rute daftar,
  // jadi tidak masalah diarahkan ke halaman yang sama.
  sales: { list: "/sales" },
  acquisitions: { list: "/acquisitions" },
  waiting_list: { list: "/waiting-list" },
  // Retur penjualan & pembelian berbagi satu modul & satu halaman (tab), tidak
  // ada rute :id.
  retur: { list: "/retur" },
  servis: { list: "/servis" },
  pelanggan: { list: "/pelanggan" },

  // ---- HRIS & Admin ----
  employees: { list: "/employees" },
  assets: { list: "/assets" },
  // JEBAKAN: backend mencatat modul "my_kpi" untuk PATCH/DELETE /kpi-custom
  // (lihat backend/routers/kpi_targets.py baris ~221-262), TAPI endpoint itu
  // digerbangi `require_role("admin","superadmin")` dan dokumennya (koleksi
  // `kpi_custom`) dikelola dari halaman ADMIN KpiTargets.jsx (rute
  // /kpi-custom, mod "sales_targets" di Layout.jsx) — BUKAN dari portal
  // karyawan MyKpi.jsx (rute /my-kpi, mod "my_kpi"). Mengarahkan ke /my-kpi
  // akan mendarat di halaman yang salah (portal pribadi, bukan daftar KPI
  // yang baru diedit/dihapus admin). Jadi path tujuan = /kpi-custom, TAPI
  // izin yang dicek = "sales_targets" (izin yang sungguhan menggerbangi
  // halaman itu), bukan "my_kpi".
  my_kpi: { list: "/kpi-custom", permModul: "sales_targets" },

  // ---- Inventory ----
  inventory_produk: { list: "/inventory/produk" },
  inventory_po: { list: "/inventory/pembelian" },
  // Gudang & Transfer tidak punya rute sendiri — keduanya bagian dari satu
  // halaman "Stok & Gudang" (Layout.jsx menggerbanginya dengan salah satu
  // dari 5 modul lewat `mods: [...]`, termasuk dua ini).
  inventory_gudang: { list: "/inventory/stok" },
  inventory_transfer: { list: "/inventory/stok" },
  stock_request: { list: "/request-stok" },
  // Internal Memo (brief 4 Sep 2026 P13): backend mencatat modul
  // "internal_memo" untuk approve/tolak/batal memo. Kunci izin `internal_memo`
  // HANYA punya aksi `update` (approver) — `can("internal_memo","view")` false
  // untuk semua orang, jadi tautannya tidak akan pernah bisa diklik kalau
  // permModul dibiarkan default. Halaman /internal-memo sendiri digerbangi
  // `inventory_opname` (Layout.jsx `mods`), maka itu yang dicek — pola `my_kpi`.
  internal_memo: { list: "/internal-memo", permModul: "inventory_opname" },

  // ---- Marketing ----
  marketing_ads: { list: "/marketing/ads" },
  marketing_budget: { list: "/marketing/budget" },
  // Satu-satunya modul dengan rute detail per-dokumen yang aman ditautkan:
  // SEMUA pemanggilan catat_aktivitas(..., "marketing_campaign", ...) di
  // campaign.py memakai `cid` = id campaign itu sendiri (bukan id entitas
  // lain), dan CampaignDetail.jsx (rute /marketing/campaigns/:id) memang
  // menerima id campaign lewat useParams. Untuk aksi "hapus" tetap diarahkan
  // ke daftar (lihat rutaUntukAktivitas) karena dokumennya sudah tidak ada.
  marketing_campaign: {
    list: "/marketing/campaigns",
    detail: (id) => `/marketing/campaigns/${id}`,
  },
  marketing_content: { list: "/marketing/content" },
  marketing_expense: { list: "/marketing/expense" },
  marketing_lead: { list: "/marketing/leads" },
  marketing_pfp: { list: "/marketing/pfp" },
  // SENGAJA tidak diberi `detail`, beda dari marketing_campaign. kol.py
  // mencatat modul "marketing_kol" untuk DUA jenis id yang berbeda di bawah
  // satu kunci: `did` (id deal KOL) untuk approve/reject/bayar-DP/pelunasan/
  // hapus-deal, dan `kid` (id KOL) untuk hapus-KOL — sementara satu-satunya
  // rute detail yang ada, /marketing/kol/:id (KolDetail.jsx), HANYA menerima
  // id KOL. Menautkan id deal ke rute itu akan mendarat di profil KOL yang
  // salah (atau error kalau id-nya bukan KOL yang valid). Tidak ada cara
  // membedakan keduanya hanya dari field `modul`+`target_id` di log aktivitas,
  // jadi diarahkan ke daftar saja — aman untuk kedua jenis id.
  marketing_kol: { list: "/marketing/kol" },
  marketing_promo: { list: "/marketing/promo-codes" },
};

/**
 * Tentukan tujuan navigasi untuk satu baris log aktivitas, atau `null` kalau
 * modulnya tidak dikenal / sengaja tidak ditautkan.
 *
 * TIDAK memeriksa izin — pemanggil (Dashboard.jsx) wajib membungkusnya dengan
 * `can(hasil.permModul, "view")` sebelum menampilkan sebagai tautan. Fungsi
 * ini murni menentukan PATH, bukan otorisasi.
 */
export function rutaUntukAktivitas(item) {
  const peta = RUTA_AKTIVITAS[item?.modul];
  if (!peta) return null;
  const permModul = peta.permModul || item.modul;
  // Aksi "hapus" → dokumennya sudah tidak ada. Arahkan ke daftar, bukan ke
  // rute detail (kalaupun ada) supaya tidak mendarat di halaman kosong/error.
  if (item.aksi !== "hapus" && peta.detail && item.target_id) {
    return { to: peta.detail(item.target_id), permModul };
  }
  return peta.list ? { to: peta.list, permModul } : null;
}
